import { ArrowDownToLine, Loader2, NotebookPen, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDefinition } from '@/components/shared/column-config-dialog';
import { ColumnConfigDialog } from '@/components/shared/column-config-dialog';
import type { DataTableColumn } from '@/components/shared/data-table';
import { DataTable } from '@/components/shared/data-table';
import { EditableNote } from '@/components/shared/editable-note';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useDeleteBulkCaseNotes, useDeleteCaseNote, useUpdateCaseNote } from '@/hooks/useCaseNoteMutations';
import { useDeleteBulkCustomerNotes, useDeleteCustomerNote, useUpdateCustomerNote } from '@/hooks/useCustomerNotes';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDeleteBulkDisputeNotes, useDeleteDisputeNote, useUpdateDisputeNote } from '@/hooks/useDisputeNotes';
import { useExportAll } from '@/hooks/useExportAll';
import { useModeAwareExport } from '@/hooks/useModeAwareExport';
import {
  buildNotesFilterVariable,
  getFilteredMockNotes,
  useNotesCount,
  useNotesListGraphQL,
} from '@/hooks/useNotesListGraphQL';
import { useDeleteBulkPromiseNotes, useDeletePromiseNote, useUpdatePromiseNote } from '@/hooks/usePromiseNoteMutations';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import {
  useDeleteBulkTransactionNotes,
  useDeleteTransactionNote,
  useUpdateTransactionNote,
} from '@/hooks/useTransactionNotes';
import { formatDateTime } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import type { BulkDeleteNotesApiResponse, NoteEntityType, NoteListItem } from '@/types/note-list';

// ── Column definitions ─────────────────────────────────────────────────────────

const DEFAULT_NOTE_COLUMNS: ColumnDefinition[] = [
  { id: 'entityType', label: 'Type', visible: true, order: 0 },
  { id: 'entityReference', label: 'Related To', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'customerAccountNumber', label: 'Account', visible: false, order: 3 },
  { id: 'noteType', label: 'Note Type', visible: true, order: 4 },
  { id: 'content', label: 'Note', visible: true, order: 5 },
  { id: 'createdByName', label: 'Author', visible: true, order: 6 },
  { id: 'createdAt', label: 'Created', visible: true, order: 7 },
  { id: 'updatedByName', label: 'Updated By', visible: false, order: 8 },
  { id: 'updatedAt', label: 'Updated', visible: false, order: 9 },
  { id: 'flags', label: 'Flags', visible: false, order: 10 },
];

const SORTABLE_COLUMNS = new Set([
  'entityType',
  'entityReference',
  'customerName',
  'customerAccountNumber',
  'noteType',
  'content',
  'createdByName',
  'createdAt',
  'updatedByName',
  'updatedAt',
]);

const TRUNCATED_COLUMN_WIDTHS: Record<string, number> = {
  content: 420,
  entityReference: 180,
  customerName: 200,
};

const ENTITY_TYPE_LABELS: Record<NoteEntityType, string> = {
  customer: 'Customer',
  transaction: 'Transaction',
  case: 'Case',
  promise: 'Promise',
  dispute: 'Dispute',
};

const ENTITY_TYPE_OPTIONS = Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => ({ value, label }));

/**
 * Note types stored on the canonical note tables: the system-written ones (NoteDefaults) plus the
 * case-note type lookup (GET /case-note-types) that callers may stamp on a note.
 */
const NOTE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'case_creation', label: 'Case creation' },
  { value: 'closure', label: 'Closure' },
  { value: 'broken', label: 'Broken promise' },
  { value: 'partial_payment', label: 'Partial payment' },
  { value: 'closed', label: 'Closed' },
  { value: 'call_summary', label: 'Call summary' },
  { value: 'action_item', label: 'Action item' },
  { value: 'follow_up', label: 'Follow up' },
  { value: 'escalation', label: 'Escalation' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'payment_arrangement', label: 'Payment arrangement' },
  { value: 'customer_feedback', label: 'Customer feedback' },
];

const NOTE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  NOTE_TYPE_OPTIONS.map((option) => [option.value, option.label]),
);

const SYSTEM_AUTHOR = 'System';

/**
 * Maps the grid's column ids to NoteList property names so the server-side export job resolves each
 * column over v_Notes_List. The grid's combined Flags cell exports as its two underlying booleans.
 */
const EXPORT_COLUMN_NAMES: Record<string, string[]> = {
  entityType: ['EntityType'],
  entityReference: ['EntityReference'],
  customerName: ['CustomerName'],
  customerAccountNumber: ['CustomerAccountNumber'],
  noteType: ['NoteType'],
  content: ['Content'],
  createdByName: ['CreatedByName'],
  createdAt: ['CreatedAt'],
  updatedByName: ['UpdatedByName'],
  updatedAt: ['UpdatedAt'],
  flags: ['IsPinned', 'IsPrivate'],
};

const FLAG_COLUMN_LABELS: Record<string, string> = {
  IsPinned: 'Pinned',
  IsPrivate: 'Private',
};

function toExportColumns(columns: ColumnDefinition[]): ColumnInput[] {
  return columns
    .filter((column) => column.id in EXPORT_COLUMN_NAMES)
    .flatMap((column) =>
      EXPORT_COLUMN_NAMES[column.id].map((name) => ({
        name,
        displayName: FLAG_COLUMN_LABELS[name] ?? column.label,
      })),
    );
}

// Demo mode has no export job to run, so the same columns are rendered client-side,
// matching how the grid renders each value.
const DEMO_EXPORT_VALUES: Record<string, (note: NoteListItem) => string> = {
  EntityType: (note) => ENTITY_TYPE_LABELS[note.entityType],
  EntityReference: (note) => note.entityReference ?? `#${note.entityId}`,
  CustomerName: (note) => note.customerName ?? '',
  CustomerAccountNumber: (note) => note.customerAccountNumber ?? '',
  NoteType: (note) => (note.noteType ? (NOTE_TYPE_LABELS[note.noteType] ?? note.noteType) : ''),
  Content: (note) => note.content,
  CreatedByName: (note) => note.createdByName || SYSTEM_AUTHOR,
  CreatedAt: (note) => formatDateTime(note.createdAt),
  UpdatedByName: (note) => note.updatedByName ?? '',
  UpdatedAt: (note) => (note.updatedAt ? formatDateTime(note.updatedAt) : ''),
  IsPinned: (note) => String(note.isPinned),
  IsPrivate: (note) => String(note.isPrivate),
};

function escapeCsv(value: string): string {
  if (value === '') return '';
  const escaped = value.replace(/"/g, '""');
  return /[",\n\r]/.test(value) ? `"${escaped}"` : escaped;
}

function downloadCsv(fileName: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function buildNotesCsv(columns: ColumnInput[], notes: NoteListItem[]): string {
  const header = columns.map((column) => escapeCsv(column.displayName ?? column.name)).join(',');
  const rows = notes.map((note) =>
    columns.map((column) => escapeCsv(DEMO_EXPORT_VALUES[column.name]?.(note) ?? '')).join(','),
  );

  return [header, ...rows].join('\n');
}

// Note ids are only unique within their own domain table, so rows are keyed and
// selected by domain and id together.
function noteRowId(note: NoteListItem): string {
  return `${note.entityType}-${note.noteId}`;
}

/**
 * Detail route for the note's parent, or undefined when the domain has no detail screen.
 * Transactions have no detail route of their own — they open from the customer's transactions tab.
 */
function buildEntityLink(note: NoteListItem): string | undefined {
  switch (note.entityType) {
    case 'customer':
      return `/customers/${note.entityId}`;
    case 'case':
      return `/collections/queue/case/${note.entityId}`;
    case 'promise':
      return `/collections/promises/${note.entityId}`;
    case 'dispute':
      return `/disputes/all/${note.entityId}`;
    default:
      return note.customerId ? `/customers/${note.customerId}?tab=transactions` : undefined;
  }
}

// ── Page component ─────────────────────────────────────────────────────────────

function NotesPageContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<string, 'asc' | 'desc'>(
    UniqueTableNamesForColumnConfiguration.Notes,
    'createdAt',
    'desc',
  );

  const {
    columns: noteColumns,
    visibleColumns,
    savePreferences,
    resetToDefaults,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Notes, DEFAULT_NOTE_COLUMNS);

  const listParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      entityType: entityTypeFilter,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      sortBy: sortField,
      sortOrder: sortDirection,
    }),
    [debouncedSearch, entityTypeFilter, dateFrom, dateTo, sortField, sortDirection],
  );

  const { items: notes, isLoading, isFetchingMore, hasMore, loadMore, refetch } = useNotesListGraphQL(listParams);
  const { data: totalCount, refetch: refetchCount } = useNotesCount(listParams);

  // A new result set invalidates the selection: the selected rows may no longer be listed.
  useEffect(() => setSelectedIds(new Set()), [listParams]);

  // Row actions reuse the per-domain endpoints the entity panels already use.
  const updateCustomerNote = useUpdateCustomerNote();
  const deleteCustomerNote = useDeleteCustomerNote();
  const updateTransactionNote = useUpdateTransactionNote();
  const deleteTransactionNote = useDeleteTransactionNote();
  const updateCaseNote = useUpdateCaseNote();
  const deleteCaseNote = useDeleteCaseNote();
  const updatePromiseNote = useUpdatePromiseNote();
  const deletePromiseNote = useDeletePromiseNote();
  const updateDisputeNote = useUpdateDisputeNote();
  const deleteDisputeNote = useDeleteDisputeNote();

  const deleteBulkCustomerNotes = useDeleteBulkCustomerNotes();
  const deleteBulkTransactionNotes = useDeleteBulkTransactionNotes();
  const deleteBulkCaseNotes = useDeleteBulkCaseNotes();
  const deleteBulkPromiseNotes = useDeleteBulkPromiseNotes();
  const deleteBulkDisputeNotes = useDeleteBulkDisputeNotes();

  const updateNote = useCallback(
    async (note: NoteListItem, content: string) => {
      const noteId = String(note.noteId);
      switch (note.entityType) {
        case 'customer':
          await updateCustomerNote.mutateAsync({ customerId: String(note.entityId), noteId, content });
          break;
        case 'transaction':
          await updateTransactionNote.mutateAsync({ transactionId: note.entityId, noteId, content });
          break;
        case 'case':
          await updateCaseNote.mutateAsync({ caseId: String(note.entityId), noteId, content });
          break;
        case 'promise':
          await updatePromiseNote.mutateAsync({ promiseId: String(note.entityId), noteId, content });
          break;
        default:
          await updateDisputeNote.mutateAsync({ disputeId: String(note.entityId), noteId, content });
      }
    },
    [updateCustomerNote, updateTransactionNote, updateCaseNote, updatePromiseNote, updateDisputeNote],
  );

  const deleteNote = useCallback(
    async (note: NoteListItem) => {
      const noteId = String(note.noteId);
      switch (note.entityType) {
        case 'customer':
          await deleteCustomerNote.mutateAsync({ customerId: String(note.entityId), noteId });
          break;
        case 'transaction':
          await deleteTransactionNote.mutateAsync({ transactionId: note.entityId, noteId });
          break;
        case 'case':
          await deleteCaseNote.mutateAsync({ caseId: String(note.entityId), noteId });
          break;
        case 'promise':
          await deletePromiseNote.mutateAsync({ promiseId: String(note.entityId), noteId });
          break;
        default:
          await deleteDisputeNote.mutateAsync({ disputeId: String(note.entityId), noteId });
      }
    },
    [deleteCustomerNote, deleteTransactionNote, deleteCaseNote, deletePromiseNote, deleteDisputeNote],
  );

  const handleSaveNote = useCallback(
    async (note: NoteListItem, content: string) => {
      try {
        await updateNote(note, content);
        toast.success('Note updated');
        refetch();
      } catch {
        toast.error('Failed to update the note');
        throw new Error('update-failed');
      }
    },
    [updateNote, refetch],
  );

  const handleDeleteNote = useCallback(
    async (note: NoteListItem) => {
      try {
        await deleteNote(note);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(noteRowId(note));
          return next;
        });
        toast.success('Note deleted');
        refetch();
        refetchCount();
      } catch {
        toast.error('Failed to delete the note');
        throw new Error('delete-failed');
      }
    },
    [deleteNote, refetch, refetchCount],
  );

  const handleBulkDelete = useCallback(async () => {
    const selectedNotes = notes.filter((note) => selectedIds.has(noteRowId(note)));
    if (selectedNotes.length === 0) {
      // The selected rows are no longer listed (e.g. deleted elsewhere and refetched).
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      return;
    }

    // Notes are deleted through the per-domain bulk endpoints, so a mixed selection
    // is split by domain and each group is deleted in a single call.
    const byDomain = new Map<NoteEntityType, number[]>();
    for (const note of selectedNotes) {
      byDomain.set(note.entityType, [...(byDomain.get(note.entityType) ?? []), note.noteId]);
    }

    const mutations: Record<NoteEntityType, (noteIds: number[]) => Promise<BulkDeleteNotesApiResponse>> = {
      customer: (noteIds) => deleteBulkCustomerNotes.mutateAsync({ noteIds }),
      transaction: (noteIds) =>
        deleteBulkTransactionNotes.mutateAsync({
          noteIds,
          transactionIds: [
            ...new Set(selectedNotes.filter((x) => x.entityType === 'transaction').map((x) => x.entityId)),
          ],
        }),
      case: (noteIds) => deleteBulkCaseNotes.mutateAsync({ noteIds }),
      promise: (noteIds) => deleteBulkPromiseNotes.mutateAsync({ noteIds }),
      dispute: (noteIds) => deleteBulkDisputeNotes.mutateAsync({ noteIds }),
    };

    setIsBulkDeleting(true);
    try {
      // One domain call failing must not hide the notes the other calls deleted,
      // so every group is settled and its notes counted as failed on rejection.
      const groups = [...byDomain];
      const settled = await Promise.allSettled(groups.map(([entityType, noteIds]) => mutations[entityType](noteIds)));

      let deletedCount = 0;
      let failedCount = 0;
      settled.forEach((outcome, index) => {
        if (outcome.status === 'fulfilled') {
          deletedCount += outcome.value.deletedCount;
          failedCount += outcome.value.failedNoteIds.length;
        } else {
          failedCount += groups[index][1].length;
        }
      });

      if (deletedCount === 0) {
        toast.error('Failed to delete the selected notes');
      } else if (failedCount > 0) {
        toast.warning(`${deletedCount} note(s) deleted, ${failedCount} could not be deleted`);
      } else {
        toast.success(`${deletedCount} note(s) deleted`);
      }

      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      refetch();
      refetchCount();
    } finally {
      setIsBulkDeleting(false);
    }
  }, [
    notes,
    selectedIds,
    deleteBulkCustomerNotes,
    deleteBulkTransactionNotes,
    deleteBulkCaseNotes,
    deleteBulkPromiseNotes,
    deleteBulkDisputeNotes,
    refetch,
    refetchCount,
  ]);

  // Export reuses the platform export-job chain: a background job renders every filtered row over
  // v_Notes_List for the requested columns. Demo mode renders the same columns client-side instead.
  const getExportColumns = useCallback((): ColumnInput[] => toExportColumns(visibleColumns), [visibleColumns]);

  const getAllExportColumns = useCallback((): ColumnInput[] => toExportColumns(DEFAULT_NOTE_COLUMNS), []);

  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const { summary: _summary, ...filter } = buildNotesFilterVariable(listParams) ?? {};

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [listParams]);

  const getExportSummary = useCallback((): string | undefined => debouncedSearch || undefined, [debouncedSearch]);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Notes',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `notes-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Notes',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `notes-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const exportDemoCsv = useCallback(
    (columns: ColumnInput[], fileName: string) => {
      downloadCsv(fileName, buildNotesCsv(columns, getFilteredMockNotes(listParams)));
      toast.success('Export downloaded');
    },
    [listParams],
  );

  const exportDemoCurrentView = useCallback(
    () => exportDemoCsv(getExportColumns(), 'notes-export.csv'),
    [exportDemoCsv, getExportColumns],
  );

  const exportDemoAllColumns = useCallback(
    () => exportDemoCsv(getAllExportColumns(), 'notes-all-columns-export.csv'),
    [exportDemoCsv, getAllExportColumns],
  );

  const handleExportCurrentView = useModeAwareExport(exportDemoCurrentView, triggerExportCurrentView);
  const handleExportAllColumns = useModeAwareExport(exportDemoAllColumns, triggerExportAllColumns);

  const tableColumns: DataTableColumn[] = useMemo(
    () =>
      visibleColumns.map((column) => ({
        id: column.id,
        label: column.label,
        sortKey: SORTABLE_COLUMNS.has(column.id) ? column.id : undefined,
        defaultWidth: TRUNCATED_COLUMN_WIDTHS[column.id],
        truncate: column.id in TRUNCATED_COLUMN_WIDTHS && column.id !== 'content' ? 'whenSized' : undefined,
      })),
    [visibleColumns],
  );

  const handleSort = useCallback(
    (field: string) => {
      setSort(field, field === sortField && sortDirection === 'asc' ? 'desc' : 'asc');
    },
    [sortField, sortDirection, setSort],
  );

  const handleSelectRow = useCallback((id: string | number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => setSelectedIds(checked ? new Set(notes.map(noteRowId)) : new Set()),
    [notes],
  );

  const renderCell = useCallback(
    (columnId: string, note: NoteListItem) => {
      switch (columnId) {
        case 'entityType':
          return <Badge variant="outline">{ENTITY_TYPE_LABELS[note.entityType]}</Badge>;
        case 'entityReference': {
          const href = buildEntityLink(note);
          const label = note.entityReference ?? `#${note.entityId}`;
          return href ? (
            <Link to={href} className="text-primary hover:underline">
              {label}
            </Link>
          ) : (
            label
          );
        }
        case 'customerName':
          return note.customerId ? (
            <Link to={`/customers/${note.customerId}`} className="text-primary hover:underline">
              {note.customerName ?? `#${note.customerId}`}
            </Link>
          ) : (
            (note.customerName ?? '-')
          );
        case 'customerAccountNumber':
          return note.customerAccountNumber ?? '-';
        case 'noteType':
          return note.noteType ? (
            <Badge variant="outline">{NOTE_TYPE_LABELS[note.noteType] ?? note.noteType}</Badge>
          ) : (
            '-'
          );
        case 'content':
          return (
            <EditableNote
              content={note.content}
              onSave={(content) => handleSaveNote(note, content)}
              onDelete={() => handleDeleteNote(note)}
            />
          );
        case 'createdByName':
          return note.createdByName || SYSTEM_AUTHOR;
        case 'createdAt':
          return formatDateTime(note.createdAt);
        case 'updatedByName':
          return note.updatedByName ?? '-';
        case 'updatedAt':
          return note.updatedAt ? formatDateTime(note.updatedAt) : '-';
        case 'flags':
          return note.isPrivate || note.isPinned ? (
            <div className="flex gap-1">
              {note.isPinned && <Badge variant="secondary">Pinned</Badge>}
              {note.isPrivate && <Badge variant="secondary">Private</Badge>}
            </div>
          ) : (
            '-'
          );
        default:
          return null;
      }
    },
    [handleSaveNote, handleDeleteNote],
  );

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <NotebookPen className="h-6 w-6" />
          <h1 className="text-2xl font-semibold tracking-tight">Notes</h1>
          <span className="text-sm text-muted-foreground">
            {totalCount !== undefined
              ? `${totalCount} note(s) across customers, transactions, cases, promises and disputes`
              : 'Notes across customers, transactions, cases, promises and disputes'}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search note, author, customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="w-[220px]">
          <MultiSelect
            options={ENTITY_TYPE_OPTIONS}
            placeholder="Select entities..."
            emptyMessage="No entities found."
            value={entityTypeFilter}
            onValueChange={setEntityTypeFilter}
          />
        </div>

        <div className="flex items-center gap-1">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
        </div>
        <div className="flex items-center gap-1">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {selectedIds.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete {selectedIds.size} selected
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowDownToLine className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCurrentView}>Export Current View</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ColumnConfigDialog columns={noteColumns} onApply={savePreferences} onReset={resetToDefaults} />
        </div>
      </div>

      <Card className="flex flex-col flex-1 min-h-0">
        <CardContent className="p-0 flex flex-col flex-1 min-h-0">
          <DataTable<NoteListItem>
            columns={tableColumns}
            data={notes}
            getRowId={noteRowId}
            renderCell={renderCell}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            showCheckbox
            selectedIds={selectedIds}
            allSelected={notes.length > 0 && selectedIds.size === notes.length}
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectRow}
            isLoading={isLoading}
            initialColumnOrder={columnOrder}
            initialColumnWidths={columnWidths}
            initialPinnedLeft={pinnedLeft}
            initialPinnedRight={pinnedRight}
            onColumnOrderChange={setColumnOrder}
            onColumnWidthsChange={setColumnWidths}
            onPinnedChange={(left, right) => setPinned(left, right)}
            onResetColumnOrder={resetColumnOrder}
            onResetColumnWidths={resetColumnWidths}
            emptyContent={
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <NotebookPen className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No notes found</h3>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
              </div>
            }
          />
          {hasMore && (
            <div className="flex justify-center border-t p-3">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={isFetchingMore}>
                {isFetchingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Notes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} note(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Radix closes the dialog on action click; keep it open until the deletes resolve
                e.preventDefault();
                void handleBulkDelete();
              }}
              disabled={isBulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function NotesPage() {
  return (
    <ExportProvider>
      <NotesPageContent />
    </ExportProvider>
  );
}
